import { BinaryModel } from '@ctw/shared/api/fhir/models/binary';
import { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import { EncounterModel } from '@ctw/shared/api/fhir/models/encounter';
import { EpisodeOfCareModel } from '@ctw/shared/api/fhir/models/episode-of-care';
import { connectionOrEmpty, fqsRequest } from '@ctw/shared/api/fqs/client';
import type { InferQueryResult } from '@ctw/shared/api/fqs/helpers';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import { PatientRecordSearchDocument } from '@ctw/shared/types/graphql/__generated__/graphql';
import { QUERY_KEY_RESOURCE_SEARCH } from '@ctw/shared/utils/query-keys';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Binary, DiagnosticReport, Encounter, EpisodeOfCare } from 'fhir/r4';
import { compact } from 'lodash-es';

export type PatientRecordSearchQueryResult = InferQueryResult<typeof PatientRecordSearchDocument>;

export type PatientRecordSearchQuerySearchResultNode = NonNullable<
  PatientRecordSearchQueryResult['SearchConnection']
>['edges'][number]['node'];
export type PatientRecordSearchQueryMatchResult =
  PatientRecordSearchQuerySearchResultNode['matches'][number];

export interface PatientRecordSearchResult
  extends Omit<PatientRecordSearchQuerySearchResultNode, 'resource'> {
  allMatches: Array<PatientRecordSearchQueryMatchResult>;
  resource: BinaryModel | DiagnosticReportModel | EncounterModel | EpisodeOfCareModel;
}

export interface PatientRecordSearchBinaryResult
  extends Omit<PatientRecordSearchQuerySearchResultNode, 'resource'> {
  allMatches: Array<PatientRecordSearchQueryMatchResult>;
  resource: BinaryModel;
}

export const isPatientRecordSearchBinaryResult = (
  result: PatientRecordSearchResult,
): result is PatientRecordSearchBinaryResult => result.resource instanceof BinaryModel;

// The list of resource types the FQS ZAP search API supports.
export const PATIENT_RECORD_SEARCH_RESOURCE_TYPES = [
  'Binary',
  'DiagnosticReport',
  'Encounter',
] as const;
// EpisodeOfCare is flag-gated, so it's not in the base list above.
export type PatientRecordSearchResourceType =
  | (typeof PATIENT_RECORD_SEARCH_RESOURCE_TYPES)[number]
  | 'EpisodeOfCare';

export interface PatientRecordSearchOption {
  label: string;
  resourceTypes: ReadonlyArray<PatientRecordSearchResourceType>;
}

// The list of resource type serach options we offer the user.
export const PATIENT_RECORD_SEARCH_OPTIONS = {
  all: { label: 'All Records', resourceTypes: PATIENT_RECORD_SEARCH_RESOURCE_TYPES },
  binary: { label: 'Documents', resourceTypes: ['Binary'] },
  diagnosticReport: { label: 'Diagnostic Reports', resourceTypes: ['DiagnosticReport'] },
  encounter: { label: 'Encounters', resourceTypes: ['Encounter'] },
} as const satisfies Record<string, PatientRecordSearchOption>;

export type PatientRecordSearchOptionValue = keyof typeof PATIENT_RECORD_SEARCH_OPTIONS;

export interface PatientRecordSearchOptions {
  // Option values to render in the "Search in" select, in PATIENT_RECORD_SEARCH_OPTIONS order.
  visibleOptionValues: ReadonlyArray<PatientRecordSearchOptionValue>;
  // Resource types to actually query for a given option.
  resourceTypesFor: (
    optionValue: PatientRecordSearchOptionValue,
  ) => ReadonlyArray<PatientRecordSearchResourceType>;
}

// Single source of truth for which resource types the "Search in" select and the search query
// both use.
export function usePatientRecordSearchOptions(): PatientRecordSearchOptions {
  const hasEpisodeOfCareSearch = useFeatureFlag('fqs-zap-search-episode-of-care');
  const visibleOptionValues = Object.keys(
    PATIENT_RECORD_SEARCH_OPTIONS,
  ) as Array<PatientRecordSearchOptionValue>;

  // Options that already search Encounter also search EpisodeOfCare once the flag is on.
  const resourceTypesFor = (optionValue: PatientRecordSearchOptionValue) => {
    const baseResourceTypes: ReadonlyArray<PatientRecordSearchResourceType> =
      PATIENT_RECORD_SEARCH_OPTIONS[optionValue].resourceTypes;
    return hasEpisodeOfCareSearch && baseResourceTypes.includes('Encounter')
      ? [...baseResourceTypes, 'EpisodeOfCare' as const]
      : baseResourceTypes;
  };

  return { visibleOptionValues, resourceTypesFor };
}

export function usePatientRecordSearch(
  query: string,
  isExactMatch: boolean | undefined,
  resourceTypes: ReadonlyArray<PatientRecordSearchResourceType>,
): UseQueryResult<Array<PatientRecordSearchResult>> {
  const searchQueryParam = isExactMatch && query ? `"${query}"` : query;

  return usePatientQuery({
    queryId: QUERY_KEY_RESOURCE_SEARCH,
    queryKey: [query, isExactMatch, resourceTypes],
    queryFn: async ({ telemetry, graphqlClient, patient }) => {
      const { data } = await fqsRequest<PatientRecordSearchQueryResult>(
        telemetry,
        graphqlClient,
        PatientRecordSearchDocument.toString(),
        {
          upid: patient.UPID,
          query: searchQueryParam,
          resourceTypes,
        },
      );

      return compact(
        connectionOrEmpty(data.SearchConnection).edges.map(
          (edge): PatientRecordSearchResult | undefined => {
            const allMatches: Array<PatientRecordSearchQueryMatchResult> = [...edge.node.matches];

            let resource: PatientRecordSearchResult['resource'] | undefined;
            switch (edge.node.resourceType) {
              case 'Binary':
                resource = new BinaryModel(edge.node.resource as unknown as Binary);
                break;
              case 'DiagnosticReport':
                resource = new DiagnosticReportModel(
                  edge.node.resource as unknown as DiagnosticReport,
                );
                break;
              case 'Encounter':
                resource = new EncounterModel(edge.node.resource as unknown as Encounter, []);
                break;
              case 'EpisodeOfCare': {
                // The query aliases status to avoid colliding with Encounter's; restore it here.
                const { episodeOfCareStatus, ...episodeOfCareFields } = edge.node
                  .resource as unknown as Omit<EpisodeOfCare, 'status'> & {
                  episodeOfCareStatus: EpisodeOfCare['status'];
                };
                resource = new EpisodeOfCareModel(
                  { ...episodeOfCareFields, status: episodeOfCareStatus },
                  [],
                );
                break;
              }
              default:
                // `result.node.resource` is a `GroupedSearchNode` which we can't handle unless we know
                // the type, so ignore it and filter it out
                resource = undefined;
                break;
            }

            return resource === undefined
              ? undefined
              : {
                  ...edge.node,
                  resource,
                  allMatches,
                };
          },
        ),
      );
    },
    enabled: !!query,
  });
}
